/**
 * Cliente HTTP minimal sobre fetch nativo.
 *
 * Responsabilidades:
 * - Inyectar `Authorization: Bearer <access_token>` automáticamente cuando hay token.
 * - Si el backend devuelve 401 con código 'token_expired', intenta refresh
 *   transparente (una sola vez por request) y reintenta la request original.
 * - Si el refresh falla, dispara un evento `auth:logout` para que AuthContext
 *   limpie el estado local. NO importa AuthContext aquí (evitar ciclo).
 * - Convierte el body de error del backend en ApiError tipada.
 *
 * Decisiones explícitas:
 * - Token storage en localStorage. Migrar a httpOnly cookie es 1 cambio aquí
 *   + 1 cambio en backend (sprint 7 si XSS aparece como riesgo).
 * - Sin librería (axios, ky): fetch + 130 líneas. Si crecen los casos
 *   (cancelación, cache, retry strategies), reconsiderar en sprint 5.
 */

import { ApiError, type ApiErrorBody } from "@/types/api";

const STORAGE_KEY_ACCESS = "nexum.access_token";
const STORAGE_KEY_REFRESH = "nexum.refresh_token";

/**
 * URL base del backend. Inyectada por Vite en build time desde VITE_API_URL.
 * En tests no la usamos (mockeamos fetch directamente).
 *
 * Default: 127.0.0.1:5050 (no localhost:5000). Razón: en macOS Monterey+
 * AirPlay Receiver escucha en localhost:5000 vía IPv6 y secuestra la conexión.
 * Ver `frontend/.env.example` para detalles.
 */
export const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? "http://127.0.0.1:5050";

/** Evento que dispara el cliente cuando el refresh falla y hay que cerrar sesión. */
export const AUTH_LOGOUT_EVENT = "nexum:auth:logout";

// ---------------------------------------------------------------------
// Token storage — encapsulado para poder migrar a otro mecanismo
// (httpOnly cookies, IndexedDB) sin tocar el resto del código.
// ---------------------------------------------------------------------

export const tokenStorage = {
  getAccess(): string | null {
    return safeRead(STORAGE_KEY_ACCESS);
  },
  getRefresh(): string | null {
    return safeRead(STORAGE_KEY_REFRESH);
  },
  set(accessToken: string, refreshToken: string): void {
    safeWrite(STORAGE_KEY_ACCESS, accessToken);
    safeWrite(STORAGE_KEY_REFRESH, refreshToken);
  },
  clear(): void {
    safeRemove(STORAGE_KEY_ACCESS);
    safeRemove(STORAGE_KEY_REFRESH);
  },
};

function safeRead(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}
function safeWrite(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    /* localStorage bloqueado (Safari private, embed) — silencio */
  }
}
function safeRemove(key: string): void {
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    /* idem */
  }
}

// ---------------------------------------------------------------------
// Refresh deduplication: si dos requests fallan con 401 simultáneamente,
// solo disparamos UN refresh y ambas esperan al mismo Promise.
// ---------------------------------------------------------------------

let inFlightRefresh: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (inFlightRefresh) return inFlightRefresh;

  const refreshToken = tokenStorage.getRefresh();
  if (!refreshToken) return false;

  inFlightRefresh = (async () => {
    try {
      const resp = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!resp.ok) return false;
      const data = (await resp.json()) as {
        access_token?: string;
        refresh_token?: string;
      };
      if (!data.access_token || !data.refresh_token) return false;
      tokenStorage.set(data.access_token, data.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      // Clear AFTER the chain resolves; los waiters ya tienen su valor.
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

function emitLogout(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_LOGOUT_EVENT));
  }
}

// ---------------------------------------------------------------------
// API pública: apiFetch + helpers tipados
// ---------------------------------------------------------------------

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Si true, NO añade Authorization (para login/register/reset). */
  anonymous?: boolean;
  /** Override del API_URL para tests. */
  baseUrl?: string;
}

export async function apiFetch<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const { method = "GET", body, anonymous = false, baseUrl = API_URL } = opts;

  const doRequest = async (): Promise<Response> => {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
    };
    if (!anonymous) {
      const access = tokenStorage.getAccess();
      if (access) headers["Authorization"] = `Bearer ${access}`;
    }
    return fetch(`${baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  };

  let resp = await doRequest();

  // 401 con token expirado → intentar refresh y reintentar UNA vez.
  if (resp.status === 401 && !anonymous) {
    const errBody = await peekJson(resp);
    if (errBody?.error === "token_expired") {
      const refreshed = await tryRefresh();
      if (refreshed) {
        resp = await doRequest();
      } else {
        // Refresh falló — sesión muerta.
        tokenStorage.clear();
        emitLogout();
      }
    } else {
      // Otros 401 (token_invalid, user_invalid): también logout.
      tokenStorage.clear();
      emitLogout();
    }
  }

  // 204 No Content
  if (resp.status === 204) {
    return undefined as T;
  }

  if (!resp.ok) {
    const errBody = (await peekJson(resp)) as ApiErrorBody | null;
    // Sprint 8 S8.4 (UX-008): mapear campos oauth_* del backend a
    // camelCase del ApiError. Sólo presentes en respuestas del flujo
    // OAuth Google signup sin invite. En cualquier otro error son
    // undefined — el catch de los callers actuales sigue funcionando.
    throw new ApiError(
      errBody?.error ?? "unknown_error",
      resp.status,
      errBody?.field,
      errBody
        ? {
            oauthPending: errBody.oauth_pending,
            oauthEmail: errBody.oauth_email,
            oauthName: errBody.oauth_name,
            oauthProvider: errBody.oauth_provider,
          }
        : undefined,
    );
  }

  return (await resp.json()) as T;
}

/**
 * Lee el body como JSON sin reventar si no es JSON válido o si ya se consumió.
 * Devolvemos null en caso de error en lugar de tirar.
 */
async function peekJson(resp: Response): Promise<ApiErrorBody | null> {
  try {
    // Cloning: la response puede consumirse después.
    const cloned = resp.clone();
    return (await cloned.json()) as ApiErrorBody;
  } catch {
    return null;
  }
}
